import { Heading } from '@/components/ui/heading'
import { Spinner } from '@/components/ui/spinner'
import { selectCoversMetaState, type CoversFetchingKey } from '@/redux/slices/coverSlice'
import { useAppSelector } from '@/redux/store'
import { BaseFileEnum } from '@/typings/domain'
import { identity } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { RefreshControl, SectionList, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import useCustomTabBarHeight from '@/lib/hooks/useCustomTabBarHeight'
import Cover from './Cover'
import CoverListFooter from './CoverListFooter'

type CoverListProps = CoversFetchingKey & {
  includeFolder: boolean
  initialLoading?: boolean
  onLoadMore: (type: BaseFileEnum) => void
  onRefresh: () => void
  ListHeaderComponent?: React.ComponentType | React.ReactElement
}

export default function CoverList({ includeFolder, initialLoading, onLoadMore, onRefresh, ListHeaderComponent, ...fetchingKey }: CoverListProps) {
  const insets = useSafeAreaInsets()
  const customTabBarHeight = useCustomTabBarHeight()
  const isKeywordSearch = 'byKeyword' in fetchingKey && fetchingKey.byKeyword
  const formCoverIds = useAppSelector(state => selectCoversMetaState(state, fetchingKey, BaseFileEnum.Form).coverIds)
  const folderCoverIds = useAppSelector(
    state => selectCoversMetaState(state, fetchingKey, BaseFileEnum.FormFolder).coverIds
  )
  const formLoading = useAppSelector(state => selectCoversMetaState(state, fetchingKey, BaseFileEnum.Form).isLoading)
  const formHasNextPage = useAppSelector(
    state => selectCoversMetaState(state, fetchingKey, BaseFileEnum.Form).pageInfo.hasNextPage
  )
  const folderLoading = useAppSelector(
    state => selectCoversMetaState(state, fetchingKey, BaseFileEnum.FormFolder).isLoading
  )
  const folderHasNextPage = useAppSelector(
    state => selectCoversMetaState(state, fetchingKey, BaseFileEnum.FormFolder).pageInfo.hasNextPage
  )

  const [refreshing, setRefreshing] = useState(false)

  const isEmpty = formCoverIds.length === 0 && folderCoverIds.length === 0
  const foldersStillPaging = includeFolder && folderHasNextPage

  const sections = useMemo(() => {
    const res = []
    if (includeFolder && folderCoverIds.length > 0) {
      res.push({ title: '文件夹', data: folderCoverIds })
    }
    if (formCoverIds.length > 0 && !foldersStillPaging) {
      res.push({ title: '表单', data: formCoverIds })
    }
    return res
  }, [folderCoverIds, formCoverIds, includeFolder, foldersStillPaging])

  const renderItem = useCallback(
    ({ item: coverId, index, section }: { item: string; index: number; section: { data: string[] } }) => (
      <Cover id={coverId} showBorder={index < section.data.length - 1} />
    ),
    []
  )

  const renderSectionHeader = useCallback(
    ({ section }: { section: { title: string } }) => {
      return includeFolder && folderCoverIds.length > 0 ? (
        <Heading size="md" className="px-6 pt-2 pb-1 text-secondary-label">
          {section.title}
        </Heading>
      ) : null
    },
    [includeFolder, folderCoverIds.length]
  )

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    onRefresh()
    setRefreshing(false)
  }, [onRefresh])

  const handleLoadMore = useCallback(() => {
    if (isEmpty) return
    if (foldersStillPaging) {
      if (folderLoading) return
      onLoadMore(BaseFileEnum.FormFolder)
      return
    }
    if (formLoading || !formHasNextPage) return
    onLoadMore(BaseFileEnum.Form)
  }, [isEmpty, foldersStillPaging, folderLoading, formLoading, formHasNextPage, onLoadMore])

  return (
    <SectionList
      sections={sections}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={
        initialLoading ? (
          <View style={{ height: 300, justifyContent: 'center', alignItems: 'center' }}>
            <Spinner />
          </View>
        ) : undefined
      }
      stickySectionHeadersEnabled={false}
      keyExtractor={identity}
      className="bg-background-0"
      style={{ flex: 1 }}
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{ paddingBottom: insets.bottom + customTabBarHeight + 16 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
      onEndReached={handleLoadMore}
      onEndReachedThreshold={0.1}
      ListFooterComponent={() => (
        <CoverListFooter
          context={isKeywordSearch ? 'search' : fetchingKey.tag}
          empty={isEmpty && !initialLoading}
          loading={formLoading && !initialLoading}
        />
      )}
    />
  )
}
